import { COMPARISON_SLUGS } from "@listou/shared/constants";
import { Link } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import { COMPARISONS } from "../lib/comparisons";
import { OCCASION_PAGE_ORDER, OCCASION_PAGES } from "../lib/occasion-pages";
import { TOOLS } from "../lib/tools";
import { Logo } from "./logo";

/**
 * Header for the signed-out pages: landing, trends, product pages. On a phone the header
 * holds the way back in, and creating a list moves to a bar under the thumb. `mobileCta`
 * lets a landing put its own action there.
 */
export function SiteHeader({ signedIn, mobileCta }: { signedIn: boolean; mobileCta?: ReactNode }) {
  return (
    <>
      <header className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
        <Logo />
        <nav className="flex items-center gap-1 sm:gap-2">
          <Link to="/idees-cadeaux" className="btn-ghost hidden sm:inline-flex">
            Idées cadeaux
          </Link>
          {signedIn ? (
            <Link to="/mes-listes" className="btn">
              Mes listes
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                search={{ redirectTo: "/mes-listes" }}
                className="btn-secondary sm:hidden"
              >
                Se connecter
              </Link>
              <Link
                to="/login"
                search={{ redirectTo: "/mes-listes" }}
                className="btn-ghost hidden sm:inline-flex"
              >
                Se connecter
              </Link>
              <Link to="/nouvelle-liste" className="btn hidden sm:inline-flex">
                Créer ma liste
              </Link>
            </>
          )}
        </nav>
      </header>
      {!signedIn && (
        <MobileCtaBar startHidden={Boolean(mobileCta)}>
          {mobileCta ?? (
            <Link to="/nouvelle-liste" className="btn min-h-14 w-full text-lg">
              Créer ma liste
            </Link>
          )}
        </MobileCtaBar>
      )}
    </>
  );
}

/**
 * Takes over from the page's `data-hero-cta` button whenever it is off screen, so a phone
 * always shows that action exactly once. A page without one shows the bar throughout.
 * `startHidden` keeps the server's HTML and the first render free of a flash of the bar.
 */
function MobileCtaBar({ startHidden, children }: { startHidden: boolean; children: ReactNode }) {
  const [visible, setVisible] = useState(!startHidden);

  useEffect(() => {
    const heroCta = document.querySelector("[data-hero-cta]");
    if (!heroCta) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      setVisible(!entries.at(-1)?.isIntersecting);
    });
    observer.observe(heroCta);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-30 grid border-t-2 border-line bg-surface/95 pt-3 pr-[max(1rem,env(safe-area-inset-right))] pb-[calc(0.75rem+env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] backdrop-blur transition-[translate,visibility] duration-200 motion-reduce:transition-none sm:hidden ${
        visible ? "" : "invisible translate-y-full"
      }`}
    >
      {children}
    </div>
  );
}

/** The occasion landings, linked from every public page: they are what people search. */
export function OccasionLinks({ className = "" }: { className?: string }) {
  return (
    <nav aria-label="Listes par occasion" className={`flex flex-wrap gap-x-5 gap-y-2 ${className}`}>
      {OCCASION_PAGE_ORDER.map((occasion) => (
        <Link
          key={occasion}
          to={OCCASION_PAGES[occasion].path}
          className="hover:text-ink hover:underline"
        >
          {OCCASION_PAGES[occasion].linkLabel}
        </Link>
      ))}
    </nav>
  );
}

type FooterLink = { key: string; node: ReactNode };

/** One titled column of links. */
function FooterColumn({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <nav aria-label={title} className="grid content-start gap-3">
      <p className="text-xs font-bold tracking-wider text-ink uppercase">{title}</p>
      <ul className="grid gap-2.5">
        {links.map((link) => (
          <li key={link.key}>{link.node}</li>
        ))}
      </ul>
    </nav>
  );
}

const footerLink = "hover:text-ink hover:underline";

/**
 * Every public page links its siblings from here: the occasions, the tools, the
 * comparisons. Titled columns keep forty links readable; the legal links sit on every page.
 */
export function SiteFooter() {
  const columns: { title: string; links: FooterLink[] }[] = [
    {
      title: "Listes",
      links: [
        ...OCCASION_PAGE_ORDER.map((occasion) => ({
          key: occasion,
          node: (
            <Link to={OCCASION_PAGES[occasion].path} className={footerLink}>
              {OCCASION_PAGES[occasion].linkLabel}
            </Link>
          ),
        })),
        {
          key: "santa",
          node: (
            <Link to="/secret-santa" className={footerLink}>
              Secret Santa en ligne
            </Link>
          ),
        },
        {
          key: "groups",
          node: (
            <Link to="/listes-en-groupe" className={footerLink}>
              Listes en groupe
            </Link>
          ),
        },
      ],
    },
    {
      title: "Grossesse et bébé",
      links: [
        ...TOOLS.map((tool) => ({
          key: tool.to,
          node: (
            <Link to={tool.to} className={footerLink}>
              {tool.label}
            </Link>
          ),
        })),
        {
          key: "prenoms",
          node: (
            <Link to="/prenoms" className={footerLink}>
              Atlas des prénoms
            </Link>
          ),
        },
      ],
    },
    {
      title: "Comparatifs",
      // One link per competitor, worded as people search it.
      links: [
        ...COMPARISON_SLUGS.map((slug) => ({
          key: slug,
          node: (
            <Link to="/comparatif/$slug" params={{ slug }} className={footerLink}>
              {COMPARISONS[slug].linkLabel}
            </Link>
          ),
        })),
        {
          key: "all",
          node: (
            <Link to="/comparatif" className={footerLink}>
              Tous les comparatifs
            </Link>
          ),
        },
      ],
    },
    {
      title: "Ressources",
      links: [
        {
          key: "ideas",
          node: (
            <Link to="/idees-cadeaux" className={footerLink}>
              Idées cadeaux
            </Link>
          ),
        },
        {
          key: "birth-ideas",
          node: (
            <Link to="/idees-cadeaux-naissance" className={footerLink}>
              Idées cadeaux de naissance
            </Link>
          ),
        },
        {
          key: "blog",
          node: (
            <Link to="/blog" className={footerLink}>
              Blog
            </Link>
          ),
        },
        {
          key: "help",
          node: (
            <Link to="/aide" className={footerLink}>
              Aide
            </Link>
          ),
        },
      ],
    },
  ];

  return (
    // Room under the legal links for SiteHeader's bar, which would otherwise sit on top of them.
    <footer className="border-t-2 border-line pb-[calc(6rem+env(safe-area-inset-bottom))] text-sm text-muted sm:pb-0">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-[1.2fr_repeat(4,1fr)]">
          <div className="col-span-2 grid content-start gap-3 md:col-span-3 lg:col-span-1">
            <Logo />
            <p className="max-w-[28ch]">Les listes d'envies qui gardent la surprise.</p>
          </div>
          {columns.map((column) => (
            <FooterColumn key={column.title} {...column} />
          ))}
        </div>
        <div className="grid gap-4 border-t border-line pt-6 md:grid-cols-[1fr_auto] md:items-start md:gap-10">
          {/* Amazon's Associates agreement wants this sentence clearly visible, not only in the legal notice. */}
          <p className="max-w-[70ch]">
            Certains liens vers des boutiques sont affiliés. En tant que Partenaire Amazon, Listou
            réalise un bénéfice sur les achats remplissant les conditions requises.
          </p>
          <nav aria-label="Informations légales" className="flex gap-5">
            <Link to="/mentions-legales" className={footerLink}>
              Mentions légales
            </Link>
            <Link to="/confidentialite" className={footerLink}>
              Confidentialité
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
